/**
 * Notification Categories
 * Categories group related workflows for bulk preference management.
 * Knock workflow keys follow the pattern: {category}-{event}
 */
export const NotificationCategory = {
  SALES: 'sales',
  // Future categories:
  // INVENTORY: 'inventory',
  // ACCOUNTING: 'accounting',
  // OPERATIONS: 'operations',
} as const;

export type NotificationCategoryType = (typeof NotificationCategory)[keyof typeof NotificationCategory];

/**
 * Notification event types (the {event} half of {category}-{event})
 */
const NotificationEvent = {
  ORDER_CREATED: 'order-created',
  WORKFLOW_FAILED: 'workflow-failed',
  DIRECT_MESSAGE: 'direct-message',
  // AI Inbox v1 — thread lifecycle events.
  // Knock workflow keys for these are flat strings (no category prefix) so the
  // dashboard reflects the surface name the rest of the platform speaks: `ai-thread-*`.
  AI_THREAD_AWAITING_CLARIFICATION: 'ai-thread-awaiting-clarification',
  AI_THREAD_COMPLETED: 'ai-thread-completed',
  AI_THREAD_FAILED: 'ai-thread-failed',
  // Zatanna (iSolve / Famous) outbound-sync failure. Flat key like the AI
  // thread events — the surface speaks "Zatanna sync", not "Sales".
  ZATANNA_SYNC_FAILED: 'zatanna-sync-failed',
  // Generic AppError async surface (TAS-3246). Flat key — any AppError
  // (validation / integration / system) can be delivered through it.
  APP_ERROR: 'app-error',
  // AI order-scoring failure (TAS-3681 / #98 Phase 4). Flat key — the surface
  // is "AI order QA", not "Sales". Fired when the post-creation scorer finds a
  // genuine gate failure (a dropped line, a wrong price, a bad ref, …).
  ORDER_SCORING_FAILED: 'order-scoring-failed',
} as const;

export type NotificationEventType = (typeof NotificationEvent)[keyof typeof NotificationEvent];

/**
 * Build a Knock workflow key from category + event.
 * e.g. buildWorkflowKey('sales', 'order-created') → 'sales-order-created'
 */
function buildWorkflowKey(category: NotificationCategoryType, event: NotificationEventType): string {
  return `${category}-${event}`;
}

/**
 * Notification Types
 * Each type maps to a specific trigger event
 */
export const NotificationType = {
  AI_ORDER_CREATED: 'ai-order-created',
  EDI_ORDER_RECEIVED: 'edi-order-received',
  AI_WORKFLOW_FAILED: 'ai-workflow-failed',
  // AI Inbox v1 — thread lifecycle notifications.
  AI_THREAD_AWAITING_CLARIFICATION: 'ai-thread-awaiting-clarification',
  AI_THREAD_COMPLETED: 'ai-thread-completed',
  AI_THREAD_FAILED: 'ai-thread-failed',
  // Zatanna (iSolve / Famous) outbound-sync failure.
  ZATANNA_SYNC_FAILED: 'zatanna-sync-failed',
  // Generic AppError async notification (TAS-3246).
  APP_ERROR: 'app-error',
  // AI order-scoring failure (TAS-3681 / #98 Phase 4).
  ORDER_SCORING_FAILED: 'order-scoring-failed',
} as const;

export type NotificationTypeValue = (typeof NotificationType)[keyof typeof NotificationType];

/**
 * Workflow IDs in Knock
 * Follow the {category}-{event} naming pattern.
 */
export const NotificationWorkflow = {
  SALES_ORDER_CREATED: buildWorkflowKey(NotificationCategory.SALES, NotificationEvent.ORDER_CREATED),
  SALES_WORKFLOW_FAILED: buildWorkflowKey(NotificationCategory.SALES, NotificationEvent.WORKFLOW_FAILED),
  SALES_DIRECT_MESSAGE: buildWorkflowKey(NotificationCategory.SALES, NotificationEvent.DIRECT_MESSAGE),
  SEND_DOCUMENT: 'send-document',
  // AI Inbox v1 — flat workflow keys; the surface is "AI Inbox", not "Sales".
  AI_THREAD_AWAITING_CLARIFICATION: NotificationEvent.AI_THREAD_AWAITING_CLARIFICATION,
  AI_THREAD_COMPLETED: NotificationEvent.AI_THREAD_COMPLETED,
  AI_THREAD_FAILED: NotificationEvent.AI_THREAD_FAILED,
  ZATANNA_SYNC_FAILED: NotificationEvent.ZATANNA_SYNC_FAILED,
  // Flat workflow key; any AppError can be delivered through this one surface.
  APP_ERROR: NotificationEvent.APP_ERROR,
  // Flat workflow key; the AI order-scoring failure surface.
  ORDER_SCORING_FAILED: NotificationEvent.ORDER_SCORING_FAILED,
} as const;

/**
 * Maps NotificationType -> Workflow key
 */
export const NotificationTypeToWorkflow: Record<NotificationTypeValue, string> = {
  [NotificationType.AI_ORDER_CREATED]: NotificationWorkflow.SALES_ORDER_CREATED,
  [NotificationType.EDI_ORDER_RECEIVED]: NotificationWorkflow.SALES_ORDER_CREATED,
  [NotificationType.AI_WORKFLOW_FAILED]: NotificationWorkflow.SALES_WORKFLOW_FAILED,
  [NotificationType.AI_THREAD_AWAITING_CLARIFICATION]: NotificationWorkflow.AI_THREAD_AWAITING_CLARIFICATION,
  [NotificationType.AI_THREAD_COMPLETED]: NotificationWorkflow.AI_THREAD_COMPLETED,
  [NotificationType.AI_THREAD_FAILED]: NotificationWorkflow.AI_THREAD_FAILED,
  [NotificationType.ZATANNA_SYNC_FAILED]: NotificationWorkflow.ZATANNA_SYNC_FAILED,
  [NotificationType.APP_ERROR]: NotificationWorkflow.APP_ERROR,
  [NotificationType.ORDER_SCORING_FAILED]: NotificationWorkflow.ORDER_SCORING_FAILED,
};

/**
 * Base payload fields shared across all notifications
 */
export interface BaseNotificationPayload {
  type: NotificationTypeValue;
}

/**
 * Line item data for the email template
 */
interface OrderNotificationLineItem {
  product_name: string;
  total_price_formatted: string;
  units_formatted: string;
}

/**
 * Nested order object for the email template (event.order.*)
 */
interface OrderEmailPayload {
  number: string;
  customer_name: string;
  customer_po?: string;
  ship_date?: string;
  revenue_formatted: string;
  pallets_total: number;
  units_total: number;
  total_formatted: string;
  view_url: string;
  line_items: OrderNotificationLineItem[];
}

/**
 * Order Created Notification Payload
 * Used by both AI_ORDER_CREATED and EDI_ORDER_RECEIVED
 * Flat fields are read by the in-app notification cell; nested `order` is read by the email template.
 */
export interface OrderCreatedPayload extends BaseNotificationPayload {
  type: typeof NotificationType.AI_ORDER_CREATED | typeof NotificationType.EDI_ORDER_RECEIVED;
  order_id: number;
  order_slug: string;
  order_path: string;
  customer_name: string;
  customer_po?: string;
  total_revenue: number;
  units: number;
  pallets: number;
  ship_date?: string;
  unmapped_products?: number;
  order: OrderEmailPayload;
}

/**
 * Knock Recipient structure
 */
export interface NotificationRecipient {
  id: string;
  email: string;
  name?: string;
}

/**
 * Parameters for triggering order-created workflow
 */
export interface TriggerOrderCreatedParams {
  salesOrderId: number;
  organizationId: number;
  type: typeof NotificationType.AI_ORDER_CREATED | typeof NotificationType.EDI_ORDER_RECEIVED;
}

// ---------------------------------------------------------------------------
// AI Inbox v1 — thread lifecycle notification payloads.
// Flat fields are read by the in-app bell cell (Knock's default renderer for
// v1; TAS-3172 introduces a custom renderer). `link` is the absolute URL to
// the thread inside the AI Inbox.
// ---------------------------------------------------------------------------

/** Linked record summary surfaced in the completed-thread payload. */
interface AiThreadLinkedRecord {
  type: 'SO' | 'PO' | 'BILL';
  id: number;
  slug?: string;
  /** Lines written to the ERP this run (iSolve/Famous only). */
  line_count?: number;
}

/** Source channel of the AI thread (mirrors the Prisma enum). */
type AiThreadSource = 'CHAT' | 'EMAIL' | 'WHATSAPP';

interface AiThreadPayloadBase extends BaseNotificationPayload {
  thread_id: number;
  thread_title: string;
  source: AiThreadSource;
  link: string;
}

export interface AiThreadAwaitingClarificationPayload extends AiThreadPayloadBase {
  type: typeof NotificationType.AI_THREAD_AWAITING_CLARIFICATION;
  failed_tool: string;
  public_summary: string;
}

export interface AiThreadCompletedPayload extends AiThreadPayloadBase {
  type: typeof NotificationType.AI_THREAD_COMPLETED;
  linked_record?: AiThreadLinkedRecord;
}

export interface AiThreadFailedPayload extends AiThreadPayloadBase {
  type: typeof NotificationType.AI_THREAD_FAILED;
  error: string;
}

export interface TriggerAiThreadAwaitingClarificationParams {
  conversationId: number;
  organizationId: number;
  failedTool: string;
  publicSummary: string;
}

export interface TriggerAiThreadCompletedParams {
  conversationId: number;
  organizationId: number;
  linkedRecord?: AiThreadLinkedRecord;
}

export interface TriggerAiThreadFailedParams {
  conversationId: number;
  organizationId: number;
  error: string;
}

// ---------------------------------------------------------------------------
// Zatanna (iSolve / Famous) outbound-sync failure notification.
// Fired when createSalesOrder / addSalesOrderLine fails on the ERP connector
// (TAS-3174's failure path). Flat fields are read by the in-app bell cell;
// `link` is the absolute deep-link used by the email CTA. `error` is the
// SANITIZED message — never a raw Oracle/SQL string (see sanitizeSyncError).
// ---------------------------------------------------------------------------

export interface ZatannaSyncFailedPayload extends BaseNotificationPayload {
  type: typeof NotificationType.ZATANNA_SYNC_FAILED;
  order_id: number;
  order_slug?: string;
  /** Relative in-app path to the order, e.g. `/sales-orders/{slug}`. */
  order_path?: string;
  /** Originating AI thread, when the order came from the AI Inbox. */
  thread_id?: number;
  /** Line-item description for partial (per-line) sync failures. */
  line_item?: string;
  error: string;
  /** Absolute deep-link for the email CTA (order if it exists, else thread). */
  link: string;
}

export interface TriggerZatannaSyncFailedParams {
  organizationId: number;
  salesOrderId: number;
  orderSlug?: string;
  /** Originating AI thread id, if the order was created from a thread. */
  conversationId?: number;
  /** Line-item description for partial sync failures. */
  lineItem?: string;
  /** Raw connector error — sanitized before it reaches the payload. */
  error: string;
}

// ---------------------------------------------------------------------------
// Generic AppError async notification (TAS-3246).
// Delivers ANY AppError (validation / not_found / conflict / auth / integration
// / system) through a single Knock surface. Recipient default = the single
// acting user (foreign-org actors are guarded out in the manager). Flat fields
// are read by the in-app bell cell; `error` is the SANITIZED user message —
// never a raw SQL/Oracle string (see sanitizeSyncError). `reference_slug` +
// `trace_id` let the user quote a support code.
// ---------------------------------------------------------------------------

/** AI-thread email threading headers, forwarded so the email channel threads
 *  the notification into the originating conversation. Mirrors
 *  `AppErrorContext.emailThreading` from `@shared/errors`. */
interface AppErrorEmailThreading {
  inReplyTo?: string;
  references?: string;
  subject?: string;
  replyTo?: string;
}

export interface AppErrorPayload extends BaseNotificationPayload {
  type: typeof NotificationType.APP_ERROR;
  /** Machine code, e.g. 'PERIOD_CLOSED', 'INTEGRATION_FAILED'. */
  code: string;
  /** AppError discriminator: validation | not_found | conflict | auth | integration | system. */
  kind: string;
  /** 'user' (message shown) or 'system' (message masked, oncall paged). */
  tier: string;
  /** Sanitized user-facing message — never a raw SQL/Oracle string. */
  error: string;
  /** What the user should do next, when the error carries one. */
  suggestion?: string;
  retryable: boolean;
  /** Human-readable reference code (e.g. `smiling-otter-42`) the user can quote
   *  to support. Present only when the error surfaces its reference. */
  reference_slug?: string;
  /** Distributed-trace id for the failing operation, for support correlation. */
  trace_id?: string;
  /** Record type for the in-app deep-link, e.g. 'sales_order'. */
  record_type?: string;
  /** Record id for the in-app deep-link. */
  record_id?: string | number;
  /** Human-readable slug for slug-routed records (bill / sales_order /
   *  purchase_order), resolved from the numeric `record_id` at notify time.
   *  Those routes are keyed on the slug — a raw id would 404 — so the cell
   *  links off this field. Unset when the entity is missing/soft-deleted or the
   *  type is not slug-routed → the cell renders no CTA. */
  record_slug?: string;
  /** Originating AI thread, when the error came from the AI Inbox. */
  thread_id?: number;
  /** Absolute deep-link for the email CTA (thread, when applicable). */
  link?: string;
  /** AI-thread email threading headers, forwarded to the email template. */
  email_threading?: AppErrorEmailThreading;
}

export interface TriggerAppErrorParams {
  organizationId: number;
  /** The acting user to notify (single-actor default). A user who is not a
   *  member of `organizationId` is guarded out — never notified cross-org. */
  actorUserId?: number | null;
  /** Originating AI thread id, when the error came from the AI Inbox. Used for
   *  the deep-link + thread_id; only trusted when it belongs to this org. */
  conversationId?: number;
}

// AI order-scoring failure notification (TAS-3681). Fired OFF the hot path when the post-creation scorer finds a genuine gate failure on a created order. The message NAMES the order and the SPECIFIC failing field(s).
// The order's submitter is DELIBERATELY not notified — the scorer is nondeterministic and would storm real users. Recipients are the internal review list (config-overridable via `SCORING_ALERT_RECIPIENT_EMAILS`).
// The default staff allowlist lives SERVER-SIDE in `api/.../notification.repo.ts` — it must never ship in the web bundle.

export interface OrderScoringFailedPayload extends BaseNotificationPayload {
  type: typeof NotificationType.ORDER_SCORING_FAILED;
  /** The created order's slug — the human-facing order identity. */
  order_slug: string;
  /** Relative in-app path to the order, e.g. `/sales-orders/{slug}`. */
  order_path?: string;
  /** Which ERP created the order: `native` | `isolve` | `famous`. */
  erp: string;
  /** The scored agent type, e.g. `SALES_ORDER` | `PURCHASE_ORDER`. */
  agent_type: string;
  /** Organization the order belongs to. */
  organization_id: number;
  /** The specific fields that failed validation (human-readable one-liners). */
  failed_fields: string[];
  /** One joined sentence naming every failed field, for the email body. */
  failed_summary: string;
  /** Gate A (field correctness) verdict. */
  field_gate_passed: boolean;
  /** Gate B (item recall) verdict. */
  recall_gate_passed: boolean;
  /** Absolute deep-link for the email CTA (order detail). */
  link: string;
}

export interface TriggerOrderScoringFailedParams {
  organizationId: number;
  orderSlug: string;
  erp: string;
  agentType: string;
  failedFields: string[];
  failedSummary: string;
  fieldGatePassed: boolean;
  recallGatePassed: boolean;
}
